import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { StatsApi } from '@/services/stats/statsApi'
import { getDateRange, type DateRange } from '@/services/stats/statsRange'
import { AuthService } from '@/services/core/auth'
import { useNetwork } from '../contexts/NetworkContext'
import { ADVERTISING_STAT_METRICS } from './useGroupedStats'
import type { StatsRow } from '../components/statsColumns'

/** Per-creative metrics the stats API returns for `groupBy=CREATIVE`. */
export const CREATIVE_STAT_METRICS = ADVERTISING_STAT_METRICS

/** One creative's metrics, keyed by metric name as the API reports them. */
export type CreativeStatsRow = StatsRow

interface CreativeStatsChunk {
  creative?: { id?: string }
  [key: string]: unknown
}

/**
 * Stats for every creative that has delivered on a line item over `range`,
 * keyed by creative id.
 */
export function useCreativeStats(lineItemId: string, range: DateRange, enabled = true) {
  const { currentNetwork } = useNetwork()
  const timeZone = currentNetwork?.timeZone ?? 'UTC'
  const currency = currentNetwork?.defaultCurrency ?? 'NOK'
  const hasToken = AuthService.isAuthenticated()

  const { startDate, endDate } = useMemo(() => getDateRange(range, timeZone), [range, timeZone])

  const query = useQuery({
    queryKey: ['creative-stats', lineItemId, range, startDate, endDate, currency],
    queryFn: () =>
      StatsApi.get({
        startDate,
        endDate,
        currency,
        groupBy: 'CREATIVE',
        lineItemId,
        topN: 50,
        includeChunkRange: true,
        showFilteredBidReasons: true,
        timeout: 180000,
      }),
    enabled: enabled && hasToken && !!currentNetwork && !!lineItemId,
  })

  const statsByCreative = useMemo(() => {
    const chunks = (query.data?.chunks ?? []) as CreativeStatsChunk[]
    const byId: Record<string, CreativeStatsRow> = {}
    for (const chunk of chunks) {
      const id = chunk.creative?.id
      if (id) byId[id] = chunk as CreativeStatsRow
    }
    return byId
  }, [query.data])

  return {
    statsByCreative,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  }
}
